import {ChatMessage, ChatMessageResponse, FocusVehicle, MessageType, Role, WidgetSettings} from "./chatbotSlice";

const MAX_RETRIES = 3;

export const sendMessageToBotRequest = async (
    baseUrl: string,
    botId: string,
    chat_id: number | undefined,
    userMessage: string,
    currentUrl: string,
    messages: ChatMessage[],
    focusedVehicle?: FocusVehicle
): Promise<ChatMessageResponse[]> => {

    const prepareData = () => {
        const formattedMessages = messages.map((m) => ({
            role: m.role ? m.role : m.isBot ? "assistant" : "user",
            saved: m.saved ?? false,
            content: m.message.rowMessage,
            tool_calls: m.toolCalls,
            tool_call_id: m.toolCallId,
        }));

        formattedMessages.push({
            role: Role.USER,
            saved: false,
            content: userMessage,
            tool_calls: undefined,
            tool_call_id: undefined,
        });

        return {
            bot_id: botId,
            chat_id,
            messages: formattedMessages,
            full_url: currentUrl || "",
            currently_focused_vehicle: focusedVehicle || {},
            additional_context: focusedVehicle?.VIN
                ? `user is currently focused on a vehicle with VIN number ${focusedVehicle.VIN}` :
                focusedVehicle?.VRM
                ? `user is currently focused on a vehicle with registration number ${focusedVehicle.VRM}`: "",
        };
    };

    const fetchWithRetry = async (baseUrl: string, data: any, attempt = 1): Promise<ChatMessageResponse[]> => {
        try {
            const response = await fetch(`${baseUrl}/query`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(data),
            });

            if (response.status === 200) {
                const result = await response.json();
                return result.messages;
            } else {
                console.warn(`Attempt ${attempt} failed with status ${response.status}`);
                if (attempt < MAX_RETRIES) {
                    return fetchWithRetry(baseUrl, data, attempt + 1);
                }
            }
        } catch (error) {
            console.error(`Attempt ${attempt} failed with error`, error);
            if (attempt < MAX_RETRIES) {
                return fetchWithRetry(baseUrl, data, attempt + 1);
            }
        }

        // If all attempts fail, return fallback message
        return [
            {
                role: Role.ASSISTANT,
                content: JSON.stringify({
                    message_type: MessageType.TEXT,
                    message: "Sorry, could you please try again?",
                }),
            } as ChatMessageResponse,
        ];
    };

    const data = prepareData();
    return fetchWithRetry(baseUrl, data);
};


export const sendStripeIntentRequest = (baseUrl: string, botId: string, chatId: number, messages: ChatMessage[]): Promise<string> => {

    const body = {
        bot_id: botId,
        chat_id: chatId,
        messages: messages.map(m => {
            return {
                role: m.isBot ? "assistant" : "user",
                saved: m.saved ?? false,
                content: m.message.rowMessage ?? "",
            }
        })
    }

    return fetch(`${baseUrl}/stripe/create-intent`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(body)
        }).then(res => res.json())
        .then(res => res.client_secret);
}

export const sendConfirmStripeIntentRequest = (baseUrl: string, stripeClientSecret: string, chatId: number, bot_id: string) => {
    fetch(`${baseUrl}/stripe/confirm-intent?secret=${stripeClientSecret}&chat_id=${chatId}&bot_id=${bot_id}`)
}

export const getWidgetSettingsRequest = (baseUrl: string, botId: string): Promise<WidgetSettings> => {
    return fetch(`${baseUrl}/widget/settings/${botId}`)
        .then(res => res.json())
}
